// How the Tasks page decides what each tab lists.
//
// The list is split by status into an Active and an Inactive tab. The search box
// and the role filter narrow BOTH tabs, so each tab's count says how many tasks
// match there — an admin can see "2 inactive tasks for this role" without
// switching tabs.

export type TaskListTab = 'active' | 'inactive';

export interface TaskListItem {
  title: string;
  description?: string | null;
  status: TaskListTab;
  /** Roles the task is assigned to. Empty or missing means every role. */
  target_role_ids?: string[];
}

export interface TaskListFilters {
  /** Free text matched against title and description. */
  search: string;
  /** A role id, or '' for all roles. */
  roleId: string;
}

/** Does the task reach this role? A task with no roles listed reaches every role. */
export const taskAppliesToRole = (task: TaskListItem, roleId: string): boolean => {
  if (!roleId) return true;
  const roles = task.target_role_ids ?? [];
  return roles.length === 0 || roles.includes(roleId);
};

const matchesSearch = (task: TaskListItem, search: string): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return task.title.toLowerCase().includes(query) || Boolean(task.description?.toLowerCase().includes(query));
};

/** The tasks matching the search and role filter, split into the two tabs (order kept). */
export const splitTasksByStatus = <T extends TaskListItem>(
  tasks: readonly T[],
  filters: TaskListFilters,
): Record<TaskListTab, T[]> => {
  const out: Record<TaskListTab, T[]> = { active: [], inactive: [] };
  for (const task of tasks) {
    if (!matchesSearch(task, filters.search) || !taskAppliesToRole(task, filters.roleId)) continue;
    out[task.status === 'active' ? 'active' : 'inactive'].push(task);
  }
  return out;
};
